import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import type { ColumnConfig } from '@timeedit/ui-components';
import type { TRootState } from './store';

/** Only store what we need */
export type BaseColumnConfig = Pick<ColumnConfig, 'key' | 'hidden'>;

type TState = {
  [userId: string]: {
    [organizationId: string]: {
      [tableId: string]: {
        columns: BaseColumnConfig[];
      };
    };
  };
};

const slice = createSlice({
  name: 'persistedColumns',
  initialState: {} as TState,
  reducers: {
    updateColumns: (
      state,
      action: PayloadAction<{
        columns: ColumnConfig[];
        userId: string;
        organizationId: string;
        tableId: string;
        typeId?: number;
      }>
    ) => {
      const { organizationId, userId, tableId, typeId, columns } =
        action.payload;
      state[userId] ??= {};
      state[userId][organizationId] ??= {};
      state[userId][organizationId][makeCompositeTableId(tableId, typeId)] = {
        columns: columns.map((c) => ({ key: c.key, hidden: c.hidden })),
      };
    },
  },
});

export const { updateColumns } = slice.actions;

export const createColumnsSelector = () =>
  createSelector(
    [
      (state: TRootState) => state.persistedColumns,
      (state: TRootState) => state.auth.user?.id,
      (state: TRootState) => state.auth.user?.organizationId,
      (_: TRootState, tableId: string) => tableId,
      (_: TRootState, __: string, typeId?: number) => typeId,
    ],
    (persistedColumns, userId, organizationId, tableId, typeId) => {
      if (!organizationId || !userId) return [];
      return (
        persistedColumns[userId]?.[organizationId]?.[
          makeCompositeTableId(tableId, typeId)
        ]?.columns ?? []
      );
    }
  );

export default slice.reducer;

const makeCompositeTableId = (tableId: string, typeId?: number) => {
  if (typeId == null) return tableId;

  return `${tableId}-${typeId}`;
};
